import type { OTCRepoFieldsData } from '@src/types/OTC';
import { isRepoProductFields } from '@src/types/OTC/otcGuards';
import type { RepoDealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { getFieldTitle, getFieldTooltip } from '@src/utils/fieldDefinitionUtils';
import { repoFieldDefinitions } from '@src/utils/OTC/repoFieldDefinitions';
import {
  DurationInput,
  FormGroup,
  NumberInput,
  SearchSelect,
  Stack,
  ToggleHorizontal,
  type Leaves,
  type ToggleHorizontalOption,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import {
  getDayCountConventionLabel,
  useDayCountConventionOptions,
  type DayCountConventionOption,
} from './productLayoutTokens';
import type { OTCFieldsContext } from './types';

interface RepoProductLayoutProps {
  formData: OTCRepoFieldsData;
  disclosureContext: OTCFieldsContext & { rowData: RepoDealProductBuilderRow };
  handleFieldChange: (field: Leaves<OTCRepoFieldsData>, value: any) => void;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
}

type RepoTermType = 'Fixed' | 'Open';

const REPO_TERM_TYPE_OPTIONS: ToggleHorizontalOption<RepoTermType>[] = [
  { displayName: 'Fixed Term', value: 'Fixed' },
  { displayName: 'Open Term', value: 'Open' },
];

export const RepoProductLayout = ({
  formData,
  disclosureContext,
  handleFieldChange,
  errors = {},
  touched = {},
}: RepoProductLayoutProps) => {
  const dayCountConventionOptions = useDayCountConventionOptions();

  const dayCountConventionSelection = useMemo(() => {
    return dayCountConventionOptions.find(option => option.value === formData.dayCountConvention);
  }, [dayCountConventionOptions, formData.dayCountConvention]);

  const handleDayCountConventionChange = useCallback(
    (option: DayCountConventionOption | undefined) => {
      handleFieldChange('dayCountConvention', option?.value);
    },
    [handleFieldChange]
  );

  const termType: RepoTermType = formData.termType ?? 'Fixed';
  const isTermTypeFixed = termType === 'Fixed';

  if (!isRepoProductFields(formData)) {
    return null;
  }

  return (
    <Stack gap="spacing12" w="100%">
      <FormGroup
        label={getFieldTitle(repoFieldDefinitions.unitRepoAmount)}
        tooltip={getFieldTooltip(repoFieldDefinitions.unitRepoAmount)}
        error={touched.unitRepoAmount ? errors.unitRepoAmount : ''}
      >
        <NumberInput
          id="unit-repo-amount"
          autoComplete="off"
          min="0"
          value={formData.unitRepoAmount?.toString() || ''}
          onChange={value => handleFieldChange('unitRepoAmount', value)}
          suffix={disclosureContext.rowData.collateralAsset}
          data-testid="repo-unit-repo-amount-input"
          touched={!!touched.unitRepoAmount}
          invalid={!!errors.unitRepoAmount}
        />
      </FormGroup>

      <FormGroup
        label={getFieldTitle(repoFieldDefinitions.termType)}
        tooltip={getFieldTooltip(repoFieldDefinitions.termType)}
        error={touched.termType ? errors.termType : undefined}
        mb={4}
      >
        <ToggleHorizontal<RepoTermType>
          options={REPO_TERM_TYPE_OPTIONS}
          value={termType}
          onChange={value => handleFieldChange('termType', value)}
          data-testid="repo-term-type-toggle"
        />
      </FormGroup>

      {isTermTypeFixed && (
        <FormGroup
          label={getFieldTitle(repoFieldDefinitions.term)}
          tooltip={getFieldTooltip(repoFieldDefinitions.term)}
          error={touched.term ? errors.term : undefined}
          mb={4}
        >
          <DurationInput
            initialValue={formData.term ?? undefined}
            timeRangeUnits="HDWM"
            onChange={value => handleFieldChange('term', value)}
            placeholder="Enter custom term"
            dataTestId="repo-term-input"
          />
        </FormGroup>
      )}

      <FormGroup
        label={getFieldTitle(repoFieldDefinitions.dayCountConvention)}
        tooltip={getFieldTooltip(repoFieldDefinitions.dayCountConvention)}
        error={touched.dayCountConvention ? errors.dayCountConvention : undefined}
        mb={4}
      >
        <SearchSelect<DayCountConventionOption>
          options={dayCountConventionOptions}
          selection={dayCountConventionSelection}
          onChange={handleDayCountConventionChange}
          getLabel={getDayCountConventionLabel}
          placeholder="Select Day Count Convention"
          showClear
          data-testid="repo-day-count-convention-select"
        />
      </FormGroup>
    </Stack>
  );
};
